import { useState } from 'react';
import { Power, PowerOff, Edit, Trash2 } from 'lucide-react';
import { IconDotsVertical } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import type { Organization } from '../../types';
import { useUpdateOrganizationStatus, useDeleteOrganization } from '../../hooks/useOrganizationManagement';

interface OrganizationActionsProps {
  organization: Organization;
  onEdit?: (organization: Organization) => void;
}

export function OrganizationActions({
  organization,
  onEdit,
}: OrganizationActionsProps) {
  const { hasPermission } = useAuth();
  const updateStatus = useUpdateOrganizationStatus();
  const deleteOrganization = useDeleteOrganization();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleStatusToggle = () => {
    updateStatus.mutate({
      id: organization.id,
      is_active: !organization.is_active,
    });
  };

  const handleDelete = () => {
    deleteOrganization.mutate(organization.id);
    setShowDeleteDialog(false);
  };

  // Check if user has any relevant permissions
  const canUpdate = hasPermission('organization_management:update');
  const canDelete = hasPermission('organization_management:delete');

  // Don't render anything if user has no permissions
  if (!canUpdate && !canDelete) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="data-[state=open]:bg-muted text-muted-foreground flex size-8 ml-auto"
            size="icon"
            data-testid={`org-actions-${organization.id}`}
            disabled={updateStatus.isPending || deleteOrganization.isPending}
          >
            <IconDotsVertical />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-32">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {canUpdate && onEdit && (
            <DropdownMenuItem
              onClick={() => onEdit(organization)}
              data-testid={`edit-org-${organization.id}`}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          {canUpdate && (
            <DropdownMenuItem
              onClick={handleStatusToggle}
              disabled={updateStatus.isPending}
              data-testid={`toggle-org-status-${organization.id}`}
            >
              {organization.is_active ? (
                <>
                  <PowerOff className="mr-2 h-4 w-4" />
                  Deactivate
                </>
              ) : (
                <>
                  <Power className="mr-2 h-4 w-4" />
                  Activate
                </>
              )}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive focus:text-destructive"
                data-testid={`delete-org-${organization.id}`}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-organization-dialog">
          <DialogHeader>
            <DialogTitle>Delete Organization</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this organization? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <p className="text-sm font-medium">{organization.name}</p>
            {organization.venue_count > 0 && (
              <p className="text-sm text-amber-600">
                Warning: This organization has {organization.venue_count} venue(s)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteOrganization.isPending}
              data-testid="cancel-delete-organization"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteOrganization.isPending}
              data-testid="confirm-delete-organization"
            >
              {deleteOrganization.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
